import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Flex, Skeleton, Tooltip } from 'antd';
import { StyledFleetOperatingHoursChart } from './FleetOperatingHoursChart.styles';
import { FleetOperatingTime } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import * as authenticationSelectors from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { NoResultsEmpty } from 'lib/components/NoResultsEmpty/NoResultsEmpty';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';

export type FleetOperatingHoursChartProps = FleetOperatingTime;

export const FleetOperatingHoursChart: React.FC<FleetOperatingHoursChartProps & { isLoading: boolean }> = ({
  actualTotalOperatingTimeS = 0,
  plannedTotalOperatingTimeS = 0,
  isLoading,
}): JSX.Element => {
  const { t } = useTranslation();
  const actualTotalOperatingTimeHrs = DateTime.convertSecondsToHours(actualTotalOperatingTimeS);
  const plannedTotalOperatingTimeHrs = DateTime.convertSecondsToHours(plannedTotalOperatingTimeS);

  let actualBarHeight = '100%';
  let plannedBarHeight = '100%';

  if (actualTotalOperatingTimeHrs > plannedTotalOperatingTimeHrs) {
    plannedBarHeight = [
      parseFloat(((plannedTotalOperatingTimeHrs * 100) / actualTotalOperatingTimeHrs).toFixed(0)),
      '%',
    ].join('');
  } else if (actualTotalOperatingTimeHrs < plannedTotalOperatingTimeHrs) {
    actualBarHeight = [
      parseFloat(((actualTotalOperatingTimeHrs * 100) / plannedTotalOperatingTimeHrs).toFixed(0)),
      '%',
    ].join('');
  }
  const isGcd = useSelector(authenticationSelectors.selectHasAccessToGCD);
  const isNoData = !isLoading && actualTotalOperatingTimeS === 0 && plannedTotalOperatingTimeS === 0;

  const renderChartContent = (): React.ReactNode => {
    if (isNoData) {
      return <NoResultsEmpty />;
    }
    if (isLoading) {
      return (
        <Flex
          justify="space-between"
          align="center"
          gap={kccThemeConstants.SPACING.LG}
          style={{ width: 'auto', height: '100%' }}
        >
          <Skeleton.Button active />
          <Skeleton.Button active />
        </Flex>
      );
    }
    return (
      <>
        <div className="fleet-chart__actual" title={actualTotalOperatingTimeHrs.toFixed(0)}>
          <div className="fleet-chart__col">
            <div className="fleet-chart__wrap-cols" style={{ height: actualBarHeight }}>
              <div className="fleet-chart__blank-col">
                <p className="fleet-chart__col-value">{actualTotalOperatingTimeHrs.toFixed(0)}</p>
              </div>
              <div className="fleet-chart__actual-col" />
            </div>
          </div>
          <p className="fleet-chart__col-title">{t('overview.fleetDashboard.fleetOperatingHoursChart.actualHours')}</p>
        </div>
        {isGcd && (
          <div className="fleet-chart__planned" title={plannedTotalOperatingTimeHrs.toFixed(0)}>
            <div className="fleet-chart__col">
              <div className="fleet-chart__wrap-cols" style={{ height: plannedBarHeight }}>
                <div className="fleet-chart__planned-col" />
                <div className="fleet-chart__blank-col">
                  <p className="fleet-chart__col-value">{plannedTotalOperatingTimeHrs.toFixed(0)}</p>
                </div>
              </div>
            </div>
            <p className="fleet-chart__col-title">
              {t('overview.fleetDashboard.fleetOperatingHoursChart.plannedHours')}
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    <StyledFleetOperatingHoursChart className="fleet-chart">
      <div className="fleet-chart__title">
        <h4>{t('overview.fleetDashboard.fleetOperatingHoursChart.title')}</h4>
        <Tooltip title={t('overview.fleetDashboard.fleetOperatingHoursChart.description')}>
          <div className="fleet-chart__info-icon-wrapper">
            <SvgIcon name="information" className="fleet-chart__info-icon" />
          </div>
        </Tooltip>
      </div>

      <div className="fleet-chart__wrapper">{renderChartContent()}</div>
    </StyledFleetOperatingHoursChart>
  );
};
